import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTag,
    faPlus,
    faEdit,
    faTrash,
    faEye,
    faFilter,
    faChartLine,
    faCalendar,
    faCheckCircle,
    faInfoCircle,
    faArrowRight,
    faArrowLeft
} from '@fortawesome/free-solid-svg-icons';
import DocumentationLayout from '../../../components/documentation/DocumentationLayout';

function MarketingOfertas() {
    return (
        <DocumentationLayout>
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
                        <FontAwesomeIcon icon={faTag} className="text-purple-600 text-2xl" />
                    </div>
                    <h1 className="text-4xl font-bold text-gray-800 mb-4">
                        Gestión de Ofertas
                    </h1>
                    <p className="text-xl text-gray-600">
                        Aprende a crear y gestionar ofertas promocionales efectivas para aumentar tus ventas 
                        y fidelizar clientes.
                    </p>
                </div>

                {/* Introducción */}
                <div className="bg-white rounded-lg shadow-md p-8 mb-8">
                    <h2 className="text-2xl font-bold text-gray-800 mb-4">
                        ¿Qué son las Ofertas?
                    </h2>
                    <p className="text-gray-600 mb-4">
                        Las ofertas son promociones especiales que puedes crear para atraer clientes, aumentar ventas 
                        o fidelizar a tus clientes existentes. Con PetitPrime puedes crear diferentes tipos de ofertas, 
                        programarlas con fechas específicas, establecer límites de uso y analizar su rendimiento.
                    </p>
                    <div className="bg-purple-50 border-l-4 border-purple-500 p-4 mt-4">
                        <div className="flex items-start">
                            <FontAwesomeIcon icon={faInfoCircle} className="text-purple-600 mt-1 mr-3" />
                            <div>
                                <p className="text-purple-800 font-semibold mb-1">Tipos de Ofertas Disponibles</p>
                                <ul className="text-purple-700 text-sm space-y-1">
                                    <li>• <strong>Descuento Porcentaje:</strong> Aplica un porcentaje de descuento sobre el total</li>
                                    <li>• <strong>Descuento Fijo:</strong> Aplica una cantidad fija de descuento</li>
                                    <li>• <strong>2x1 (Dos por Uno):</strong> Oferta especial de compra dos productos por el precio de uno</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Crear Nueva Oferta */}
                <section className="mb-12">
                    <div className="flex items-center mb-6">
                        <div className="bg-purple-100 p-3 rounded-lg mr-4">
                            <FontAwesomeIcon icon={faPlus} className="text-purple-600 text-xl" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-800">
                            Crear una Nueva Oferta
                        </h2>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Paso 1: Acceder al Módulo de Ofertas
                        </h3>
                        <ol className="space-y-4 text-gray-600">
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
                                <span>Desde el menú principal, haz clic en <strong>"Ofertas"</strong> o <strong>"Marketing" → "Ofertas"</strong></span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
                                <span>Verás la lista de todas tus ofertas existentes (si las tienes)</span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">3</span>
                                <span>Haz clic en el botón <strong>"Nueva Oferta"</strong> ubicado en la parte superior derecha</span>
                            </li>
                        </ol>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Paso 2: Completar la Información Básica
                        </h3>
                        <p className="text-gray-600 mb-4">
                            El formulario de creación de ofertas tiene varias secciones. Comienza completando la información básica:
                        </p>
                        <div className="space-y-4">
                            <div className="border-l-4 border-blue-500 pl-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Información General</h4>
                                <ul className="text-gray-600 space-y-2 text-sm">
                                    <li>• <strong>Nombre:</strong> Un nombre descriptivo para tu oferta (ej: "Descuento Verano 20%")</li>
                                    <li>• <strong>Descripción:</strong> Explica claramente qué incluye la oferta</li>
                                    <li>• <strong>Marca:</strong> Selecciona la marca a la que pertenece la oferta</li>
                                    <li>• <strong>Objetivo Principal:</strong> Elige el objetivo de tu oferta:
                                        <ul className="ml-4 mt-1 space-y-1">
                                            <li>- Aumentar Ventas</li>
                                            <li>- Captar Nuevos Clientes</li>
                                            <li>- Fidelizar Clientes</li>
                                        </ul>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Paso 3: Seleccionar el Tipo de Oferta
                        </h3>
                        <p className="text-gray-600 mb-4">
                            Elige el tipo de oferta que mejor se adapte a tus necesidades:
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="border rounded-lg p-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Descuento Porcentaje</h4>
                                <p className="text-sm text-gray-600 mb-3">
                                    Aplica un porcentaje de descuento sobre el total de la compra
                                </p>
                                <div className="text-xs text-gray-500 space-y-1">
                                    <p><strong>Campos requeridos:</strong></p>
                                    <p>• Porcentaje de descuento (ej: 20)</p>
                                    <p>• Importe mínimo (opcional)</p>
                                    <p>• Importe máximo (opcional)</p>
                                </div>
                            </div>
                            <div className="border rounded-lg p-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Descuento Fijo</h4>
                                <p className="text-sm text-gray-600 mb-3">
                                    Aplica una cantidad fija de descuento (ej: 10€)
                                </p>
                                <div className="text-xs text-gray-500 space-y-1">
                                    <p><strong>Campos requeridos:</strong></p>
                                    <p>• Descuento fijo (ej: 10)</p>
                                    <p>• Importe mínimo (opcional)</p>
                                    <p>• Importe máximo (opcional)</p>
                                </div>
                            </div>
                            <div className="border rounded-lg p-4">
                                <h4 className="font-semibold text-gray-800 mb-2">2x1 (Dos por Uno)</h4>
                                <p className="text-sm text-gray-600 mb-3">
                                    Oferta especial donde el cliente compra dos productos por el precio de uno
                                </p>
                                <div className="text-xs text-gray-500 space-y-1">
                                    <p><strong>Campos requeridos:</strong></p>
                                    <p>• Cantidad mínima (generalmente 2)</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Paso 4: Configurar Fechas y Límites
                        </h3>
                        <div className="space-y-4">
                            <div className="border-l-4 border-green-500 pl-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Fechas</h4>
                                <ul className="text-gray-600 space-y-2 text-sm">
                                    <li>• <strong>Fecha de Inicio:</strong> Cuándo comenzará a estar disponible la oferta</li>
                                    <li>• <strong>Fecha de Fin:</strong> Cuándo expirará la oferta</li>
                                </ul>
                                <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mt-3">
                                    <p className="text-xs text-yellow-800">
                                        <strong>💡 Consejo:</strong> Programa tus ofertas con suficiente antelación para maximizar su impacto. 
                                        Las ofertas programadas aparecerán con estado "PROGRAMADA" hasta que llegue la fecha de inicio.
                                    </p>
                                </div>
                            </div>
                            <div className="border-l-4 border-orange-500 pl-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Límites de Uso</h4>
                                <ul className="text-gray-600 space-y-2 text-sm">
                                    <li>• <strong>Usos Máximos Globales:</strong> Número total de veces que se puede usar la oferta (mínimo 1)</li>
                                    <li>• <strong>Usos Máximos por Cliente:</strong> Cuántas veces puede usar la misma oferta un cliente (mínimo 1)</li>
                                </ul>
                            </div>
                            <div className="border-l-4 border-purple-500 pl-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Aplicabilidad</h4>
                                <p className="text-gray-600 text-sm mb-2">
                                    Define dónde se puede aplicar la oferta:
                                </p>
                                <ul className="text-gray-600 space-y-1 text-sm">
                                    <li>• <strong>Solo Online:</strong> Solo para compras en línea</li>
                                    <li>• <strong>Solo Físico:</strong> Solo para compras en tienda física</li>
                                    <li>• <strong>Ambos:</strong> Para compras online y físicas</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Paso 5: Mensaje Promocional y Call to Action
                        </h3>
                        <p className="text-gray-600 mb-4">
                            Añade un mensaje atractivo y una llamada a la acción para que tus clientes sepan cómo usar la oferta:
                        </p>
                        <div className="space-y-3">
                            <div>
                                <p className="text-sm font-semibold text-gray-700 mb-1">Mensaje Promocional</p>
                                <p className="text-sm text-gray-600">
                                    Ejemplo: "¡Aprovecha nuestro descuento especial de verano! Ahorra 20% en toda tu compra."
                                </p>
                            </div>
                            <div>
                                <p className="text-sm font-semibold text-gray-700 mb-1">Call to Action</p>
                                <p className="text-sm text-gray-600">
                                    Ejemplo: "Usa el código VERANO20 al finalizar tu compra"
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Paso 6: Etiquetas (Opcional)
                        </h3>
                        <p className="text-gray-600 mb-4">
                            Las etiquetas te ayudan a organizar y categorizar tus ofertas. Puedes añadir etiquetas como:
                        </p>
                        <ul className="text-gray-600 space-y-2 text-sm">
                            <li>• "verano", "navidad", "black-friday" (por temporada)</li>
                            <li>• "nuevos-clientes", "fidelización" (por objetivo)</li>
                            <li>• "urgente", "limitado" (por urgencia)</li>
                        </ul>
                        <p className="text-sm text-gray-500 mt-3">
                            Para añadir una etiqueta, escribe el texto en el campo y presiona Enter o haz clic en el botón "+".
                        </p>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Paso 7: Guardar la Oferta
                        </h3>
                        <ol className="space-y-3 text-gray-600">
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
                                <span>Revisa toda la información ingresada</span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
                                <span>Haz clic en <strong>"Guardar"</strong> o <strong>"Crear Oferta"</strong></span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">3</span>
                                <span>La oferta se creará con estado <strong>"BORRADOR"</strong> por defecto</span>
                            </li>
                        </ol>
                        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mt-4">
                            <p className="text-sm text-blue-800">
                                <strong>📝 Nota:</strong> Puedes guardar ofertas como borrador y activarlas más tarde cuando estés listo.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Ver y Gestionar Ofertas */}
                <section className="mb-12">
                    <div className="flex items-center mb-6">
                        <div className="bg-purple-100 p-3 rounded-lg mr-4">
                            <FontAwesomeIcon icon={faEye} className="text-purple-600 text-xl" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-800">
                            Ver y Gestionar Ofertas
                        </h2>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Vista de Lista de Ofertas
                        </h3>
                        <p className="text-gray-600 mb-4">
                            En la página principal de ofertas verás:
                        </p>
                        <ul className="space-y-3 text-gray-600">
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Tarjetas de Ofertas:</strong> Cada oferta se muestra en una tarjeta con información resumida (nombre, tipo, estado, valor, métricas)</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Estadísticas Rápidas:</strong> En la parte superior verás el total de ofertas, ofertas activas, total de usos y visualizaciones</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Acciones Rápidas:</strong> Desde cada tarjeta puedes ver detalles, editar, cambiar estado o eliminar</span>
                            </li>
                        </ul>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Ver Detalles de una Oferta
                        </h3>
                        <ol className="space-y-3 text-gray-600">
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
                                <span>Haz clic en el botón <strong>"Ver Detalles"</strong> o en la tarjeta de la oferta</span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
                                <span>Se abrirá un modal con toda la información de la oferta</span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">3</span>
                                <span>Verás información completa, métricas en tiempo real, fechas y límites de uso</span>
                            </li>
                        </ol>
                        <div className="bg-purple-50 border-l-4 border-purple-500 p-4 mt-4">
                            <p className="text-sm text-purple-800">
                                <strong>📊 Métricas Disponibles:</strong> Usos totales, visualizaciones, tasa de conversión y más.
                            </p>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Editar una Oferta
                        </h3>
                        <ol className="space-y-3 text-gray-600">
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
                                <span>Desde la tarjeta de la oferta, haz clic en el botón <strong>"Editar"</strong> (icono de lápiz)</span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
                                <span>También puedes hacer clic en <strong>"Editar"</strong> desde el modal de detalles</span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">3</span>
                                <span>Se abrirá el mismo formulario de creación, pero con los datos actuales de la oferta</span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">4</span>
                                <span>Modifica los campos que necesites y haz clic en <strong>"Guardar"</strong></span>
                            </li>
                        </ol>
                    </div>
                </section>

                {/* Filtrar y Buscar */}
                <section className="mb-12">
                    <div className="flex items-center mb-6">
                        <div className="bg-purple-100 p-3 rounded-lg mr-4">
                            <FontAwesomeIcon icon={faFilter} className="text-purple-600 text-xl" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-800">
                            Filtrar y Buscar Ofertas
                        </h2>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Búsqueda por Texto
                        </h3>
                        <p className="text-gray-600 mb-4">
                            Utiliza el campo de búsqueda en la parte superior para buscar ofertas por nombre o descripción:
                        </p>
                        <ol className="space-y-3 text-gray-600">
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
                                <span>Escribe el término de búsqueda en el campo "Buscar ofertas..."</span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
                                <span>Los resultados se filtrarán automáticamente mientras escribes</span>
                            </li>
                        </ol>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Filtros Disponibles
                        </h3>
                        <div className="space-y-4">
                            <div className="border-l-4 border-blue-500 pl-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Filtro por Tipo</h4>
                                <p className="text-gray-600 text-sm mb-2">
                                    Filtra ofertas por su tipo:
                                </p>
                                <ul className="text-gray-600 text-sm space-y-1">
                                    <li>• Todos los tipos</li>
                                    <li>• Descuento Porcentaje</li>
                                    <li>• Descuento Fijo</li>
                                    <li>• 2x1 (Dos por Uno)</li>
                                </ul>
                            </div>
                            <div className="border-l-4 border-green-500 pl-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Filtro por Estado</h4>
                                <p className="text-gray-600 text-sm mb-2">
                                    Filtra ofertas por su estado actual:
                                </p>
                                <ul className="text-gray-600 text-sm space-y-1">
                                    <li>• <strong>BORRADOR:</strong> Ofertas guardadas pero no activas</li>
                                    <li>• <strong>PROGRAMADA:</strong> Ofertas con fecha de inicio futura</li>
                                    <li>• <strong>ACTIVA:</strong> Ofertas disponibles para usar</li>
                                    <li>• <strong>PAUSADA:</strong> Ofertas temporalmente suspendidas</li>
                                    <li>• <strong>FINALIZADA:</strong> Ofertas que han expirado</li>
                                    <li>• <strong>CANCELADA:</strong> Ofertas canceladas</li>
                                </ul>
                            </div>
                            <div className="border-l-4 border-purple-500 pl-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Filtro por Marca</h4>
                                <p className="text-gray-600 text-sm">
                                    Filtra ofertas por la marca a la que pertenecen. Útil si gestionas múltiples marcas.
                                </p>
                            </div>
                        </div>
                        <div className="bg-yellow-50 border border-yellow-200 rounded p-4 mt-4">
                            <p className="text-xs text-yellow-800">
                                <strong>💡 Consejo:</strong> Puedes combinar múltiples filtros para encontrar exactamente lo que buscas. 
                                Por ejemplo, puedes buscar "verano" en ofertas activas de tipo "Descuento Porcentaje".
                            </p>
                        </div>
                    </div>
                </section>

                {/* Gestionar Estados */}
                <section className="mb-12">
                    <div className="flex items-center mb-6">
                        <div className="bg-purple-100 p-3 rounded-lg mr-4">
                            <FontAwesomeIcon icon={faCalendar} className="text-purple-600 text-xl" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-800">
                            Gestionar Estados de Ofertas
                        </h2>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Estados Disponibles
                        </h3>
                        <div className="space-y-4">
                            <div className="border rounded-lg p-4">
                                <div className="flex items-center justify-between mb-2">
                                    <h4 className="font-semibold text-gray-800">BORRADOR</h4>
                                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">Estado inicial</span>
                                </div>
                                <p className="text-sm text-gray-600">
                                    Ofertas guardadas pero no disponibles para uso. Puedes editarlas y activarlas cuando estés listo.
                                </p>
                            </div>
                            <div className="border rounded-lg p-4">
                                <div className="flex items-center justify-between mb-2">
                                    <h4 className="font-semibold text-gray-800">PROGRAMADA</h4>
                                    <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded">Automático</span>
                                </div>
                                <p className="text-sm text-gray-600">
                                    Ofertas con fecha de inicio futura. Se activarán automáticamente cuando llegue la fecha de inicio.
                                </p>
                            </div>
                            <div className="border rounded-lg p-4 border-green-200 bg-green-50">
                                <div className="flex items-center justify-between mb-2">
                                    <h4 className="font-semibold text-gray-800">ACTIVA</h4>
                                    <span className="text-xs bg-green-100 text-green-600 px-2 py-1 rounded">Disponible</span>
                                </div>
                                <p className="text-sm text-gray-600">
                                    Ofertas disponibles para ser utilizadas por los clientes. Este es el estado que debes usar para ofertas en curso.
                                </p>
                            </div>
                            <div className="border rounded-lg p-4 border-yellow-200 bg-yellow-50">
                                <div className="flex items-center justify-between mb-2">
                                    <h4 className="font-semibold text-gray-800">PAUSADA</h4>
                                    <span className="text-xs bg-yellow-100 text-yellow-600 px-2 py-1 rounded">Temporal</span>
                                </div>
                                <p className="text-sm text-gray-600">
                                    Ofertas temporalmente suspendidas. Puedes pausar una oferta activa y reactivarla más tarde sin perder datos.
                                </p>
                            </div>
                            <div className="border rounded-lg p-4">
                                <div className="flex items-center justify-between mb-2">
                                    <h4 className="font-semibold text-gray-800">FINALIZADA</h4>
                                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">Automático</span>
                                </div>
                                <p className="text-sm text-gray-600">
                                    Ofertas que han alcanzado su fecha de fin o han agotado sus usos máximos. Se marca automáticamente.
                                </p>
                            </div>
                            <div className="border rounded-lg p-4 border-red-200 bg-red-50">
                                <div className="flex items-center justify-between mb-2">
                                    <h4 className="font-semibold text-gray-800">CANCELADA</h4>
                                    <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded">Manual</span>
                                </div>
                                <p className="text-sm text-gray-600">
                                    Ofertas canceladas manualmente. Úsalo cuando necesites cancelar una oferta que ya no quieres usar.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Cambiar el Estado de una Oferta
                        </h3>
                        <ol className="space-y-3 text-gray-600">
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
                                <span>Desde la tarjeta de la oferta, haz clic en el botón de estado (pausar/activar)</span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
                                <span>También puedes cambiar el estado desde el modal de detalles</span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">3</span>
                                <span>Selecciona el nuevo estado deseado</span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">4</span>
                                <span>Confirma el cambio. El estado se actualizará inmediatamente</span>
                            </li>
                        </ol>
                    </div>
                </section>

                {/* Eliminar Ofertas */}
                <section className="mb-12">
                    <div className="flex items-center mb-6">
                        <div className="bg-purple-100 p-3 rounded-lg mr-4">
                            <FontAwesomeIcon icon={faTrash} className="text-purple-600 text-xl" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-800">
                            Eliminar Ofertas
                        </h2>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Cómo Eliminar una Oferta
                        </h3>
                        <ol className="space-y-3 text-gray-600">
                            <li className="flex items-start">
                                <span className="bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
                                <span>Desde la tarjeta de la oferta, haz clic en el botón <strong>"Eliminar"</strong> (icono de papelera)</span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
                                <span>También puedes eliminarla desde el modal de detalles</span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">3</span>
                                <span>Se mostrará una confirmación preguntando si estás seguro</span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">4</span>
                                <span>Confirma la eliminación. Esta acción no se puede deshacer</span>
                            </li>
                        </ol>
                        <div className="bg-red-50 border-l-4 border-red-500 p-4 mt-4">
                            <p className="text-sm text-red-800">
                                <strong>⚠️ Advertencia:</strong> Eliminar una oferta es permanente. Si solo quieres ocultarla temporalmente, 
                                considera cambiar su estado a "PAUSADA" o "CANCELADA" en lugar de eliminarla.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Métricas y Análisis */}
                <section className="mb-12">
                    <div className="flex items-center mb-6">
                        <div className="bg-purple-100 p-3 rounded-lg mr-4">
                            <FontAwesomeIcon icon={faChartLine} className="text-purple-600 text-xl" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-800">
                            Métricas y Análisis
                        </h2>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Métricas Disponibles
                        </h3>
                        <p className="text-gray-600 mb-4">
                            Cada oferta tiene métricas en tiempo real que te ayudan a entender su rendimiento:
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="border rounded-lg p-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Usos Totales</h4>
                                <p className="text-sm text-gray-600">
                                    Número de veces que la oferta ha sido utilizada por los clientes
                                </p>
                            </div>
                            <div className="border rounded-lg p-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Visualizaciones</h4>
                                <p className="text-sm text-gray-600">
                                    Número de veces que los clientes han visto la oferta
                                </p>
                            </div>
                            <div className="border rounded-lg p-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Tasa de Conversión</h4>
                                <p className="text-sm text-gray-600">
                                    Porcentaje de visualizaciones que se convierten en usos (usos / visualizaciones)
                                </p>
                            </div>
                            <div className="border rounded-lg p-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Usos Restantes</h4>
                                <p className="text-sm text-gray-600">
                                    Cuántos usos quedan disponibles antes de alcanzar el límite máximo
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Ver Métricas Detalladas
                        </h3>
                        <ol className="space-y-3 text-gray-600">
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
                                <span>Abre el modal de detalles de la oferta</span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
                                <span>En la sección de métricas verás todas las estadísticas en tiempo real</span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">3</span>
                                <span>También puedes acceder a la página de métricas desde el menú principal</span>
                            </li>
                        </ol>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Página de Métricas de Ofertas
                        </h3>
                        <p className="text-gray-600 mb-4">
                            La página de métricas te ofrece una vista general de todas tus ofertas:
                        </p>
                        <ul className="space-y-3 text-gray-600">
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Resumen General:</strong> Total de ofertas, ofertas activas, total de usos y visualizaciones</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Top Ofertas:</strong> Las 5 ofertas más utilizadas</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Distribución por Estado:</strong> Gráfico de ofertas por estado</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Distribución por Tipo:</strong> Gráfico de ofertas por tipo</span>
                            </li>
                        </ul>
                    </div>
                </section>

                {/* Consejos Finales */}
                <div className="bg-purple-50 rounded-lg p-8 mb-8">
                    <h3 className="text-lg font-semibold text-purple-800 mb-4">
                        💡 Consejos para Ofertas Exitosas
                    </h3>
                    <ul className="space-y-3 text-gray-700">
                        <li className="flex items-start">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-purple-600 mr-2 mt-1" />
                            <span><strong>Define objetivos claros:</strong> Antes de crear una oferta, decide qué quieres lograr (aumentar ventas, captar clientes, fidelizar)</span>
                        </li>
                        <li className="flex items-start">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-purple-600 mr-2 mt-1" />
                            <span><strong>Programa con anticipación:</strong> Crea ofertas con suficiente tiempo antes de su fecha de inicio para promocionarlas</span>
                        </li>
                        <li className="flex items-start">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-purple-600 mr-2 mt-1" />
                            <span><strong>Establece límites razonables:</strong> Los límites de uso ayudan a controlar costos y crear urgencia</span>
                        </li>
                        <li className="flex items-start">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-purple-600 mr-2 mt-1" />
                            <span><strong>Analiza el rendimiento:</strong> Revisa las métricas regularmente para entender qué funciona y qué no</span>
                        </li>
                        <li className="flex items-start">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-purple-600 mr-2 mt-1" />
                            <span><strong>Combina diferentes tipos:</strong> Varía entre descuentos porcentuales, fijos y ofertas especiales para mantener el interés</span>
                        </li>
                        <li className="flex items-start">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-purple-600 mr-2 mt-1" />
                            <span><strong>Mensajes claros:</strong> Asegúrate de que el mensaje promocional y el call to action sean claros y atractivos</span>
                        </li>
                    </ul>
                </div>

                {/* Navegación */}
                <div className="flex justify-between items-center pt-6 border-t">
                    <Link
                        to="/documentation/marketing"
                        className="text-gray-600 hover:text-gray-800 font-medium inline-flex items-center"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                        Volver a Marketing
                    </Link>
                    <Link
                        to="/documentation/integraciones"
                        className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors inline-flex items-center"
                    >
                        Siguiente: Integraciones
                        <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                    </Link>
                </div>
            </div>
        </DocumentationLayout>
    );
}

export default MarketingOfertas;

